<template>
  <div>
    <!-- 编辑组件的内容会通过props传入并在父组件中展示 -->
  </div>
</template>

<script setup>
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { fillDefaultZeroFields, convertVisionFieldsInForm } from '@/utils/opticalDataUtil';
import { isAuthRedirecting } from '@/utils/authRedirect';
import { isBetaHistoryModel, parseBetaHistoryDuration } from '@/utils/betaHistoryDuration';

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  // 是否显示移动端样式
  isMobile: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'cancel']);

const buildDurationOptions = (type, selected) => {
  const max = type === 'months' ? 120 : 31;
  const options = ['<option value="">请选择</option>'];
  for (let value = 0; value <= max; value += 1) {
    options.push(`<option value="${value}" ${String(selected) === String(value) ? 'selected' : ''}>${value}</option>`);
  }
  return options.join('');
};

const getBetaHistoryUsedValue = name =>
  document.querySelector(`input[name="${name}"]:checked`)?.value || null;

const getDurationValue = (usedName, monthsId, daysId) => {
  if (getBetaHistoryUsedValue(usedName) !== '是') return null;
  const months = document.getElementById(monthsId)?.value;
  const days = document.getElementById(daysId)?.value;
  if (months === '' && days === '') return null;
  return `${months || 0}个月${days || 0}天`;
};

const setupBetaHistoryForm = () => {
  const sections = [
    {
      usedName: 'edit-atropineEyeDropsUsed',
      durationId: 'edit-atropineEyeDropsDuration',
      monthsId: 'edit-atropineEyeDropsStopMonths',
      daysId: 'edit-atropineEyeDropsStopDays',
      elapsedId: 'edit-atropineEyeDropsElapsed',
      elapsedLabel: '已停药'
    },
    {
      usedName: 'edit-orthokeratologyLensUsed',
      durationId: 'edit-orthokeratologyLensDuration',
      monthsId: 'edit-orthokeratologyLensStopMonths',
      daysId: 'edit-orthokeratologyLensStopDays',
      elapsedId: 'edit-orthokeratologyLensElapsed',
      elapsedLabel: '已停戴'
    }
  ];

  sections.forEach(({ usedName, durationId, monthsId, daysId, elapsedId, elapsedLabel }) => {
    const duration = document.getElementById(durationId);
    const months = document.getElementById(monthsId);
    const days = document.getElementById(daysId);
    const elapsed = document.getElementById(elapsedId);
    if (!duration || !months || !days || !elapsed) return;

    const updateElapsed = () => {
      const isUsed = getBetaHistoryUsedValue(usedName) === '是';
      const hasDuration = months.value !== '' || days.value !== '';
      duration.style.display = isUsed ? 'grid' : 'none';

      if (!isUsed) {
        elapsed.hidden = true;
        return;
      }

      elapsed.hidden = !hasDuration;
      if (hasDuration) {
        elapsed.textContent = `${elapsedLabel} ${months.value || 0}个月${days.value || 0}天`;
      }
    };

    document.querySelectorAll(`input[name="${usedName}"]`).forEach(input => {
      input.addEventListener('change', updateElapsed);
    });
    months.addEventListener('change', updateElapsed);
    days.addEventListener('change', updateElapsed);
    updateElapsed();
  });
};

// 获取验配单位列表（用于编辑弹窗下拉）
const fetchOptometryClinicOptions = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.data.code === '200' && response.data.data) {
      const clinics = response.data.data || [];
      const unique = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      return unique;
    }
  } catch (e) {
    if (e?.response?.status === 401 || isAuthRedirecting()) {
      return null;
    }
    console.error('获取验配单位列表失败', e);
  }
  return [];
};

const buildCompanySelectHtml = (currentCompany, options) => {
  const cur = (currentCompany || '').trim();
  const hasInList = cur && options.includes(cur);
  let html = options.map(c =>
    `<option value="${String(c || '').replace(/"/g, '&quot;')}" ${cur === c ? 'selected' : ''}>${String(c || '').replace(/</g, '&lt;')}</option>`
  ).join('');
  if (cur && !hasInList) {
    html = `<option value="${cur.replace(/"/g, '&quot;')}" selected>${cur.replace(/</g, '&lt;')}</option>` + html;
  }
  return html;
};

const handleEdit = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 判断是否管理员
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo.role === 'ADMIN';

    // 先获取验配单位列表供下拉使用
    const optometryClinicOptions = await fetchOptometryClinicOptions();
    if (optometryClinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const companyOptionsHtml = buildCompanySelectHtml(row.company, optometryClinicOptions);
    const atropineDuration = parseBetaHistoryDuration(row.atropineEyeDropsStopDuration) || { months: '', days: '' };
    const orthokeratologyDuration = parseBetaHistoryDuration(row.orthokeratologyLensStopDuration) || { months: '', days: '' };

    const confirmPromise = ElMessageBox.confirm(
        `<div class="edit-order-form">
        <!-- 第一部分：订单基本信息 -->
        <div class="form-section basic-info">
          <h3>订单基本信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>序号：</label>
              <input type="text" class="el-input__inner" value="${displayId}" readonly>
              <input type="hidden" id="edit-id" value="${row.id || ''}">
              <input type="hidden" id="edit-orderNumber" value="${row.orderNumber || ''}">
              <input type="hidden" id="edit-userId" value="${row.userId || ''}">
              <input type="hidden" id="edit-status" value="${row.status || ''}">
            </div>
            <div class="form-item">
              <label>订单类型：</label>
              <input type="text" class="el-input__inner" value="${row.orderType || '散订单'}" id="edit-orderType" readonly>
            </div>
            <div class="form-item">
              <label>订单日期*：</label>
              <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-orderDate" readonly>
            </div>
            <div class="form-item">
              <label>患者姓名*：</label>
              <input type="text" class="el-input__inner" value="${row.patientName || ''}" id="edit-patientName" required>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>年龄*：</label>
              <input type="text" class="el-input__inner" value="${row.age || ''}" id="edit-age">
            </div>
             <div class="form-item">
              <label>验光师：</label>
              <input type="text" class="el-input__inner" value="${row.optometrist || ''}" id="edit-optometrist">
            </div>
            <div class="form-item">
              <label>地区：</label>
              <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-region">
            </div>
            <div class="form-item">
              <label>订单型号*：</label>
              <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-lensModel">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>验配单位*：</label>
              <select class="el-input__inner" id="edit-company">
                <option value="">请选择验配单位</option>
                ${companyOptionsHtml}
              </select>
            </div>
            <div class="form-item">
              <label>采购单位*：</label>
              <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-purchasingUnits" ${isAdmin ? '' : 'readonly'}>
            </div>
          </div>
        </div>
          <div class="form-section beta-history-section" style="display: ${isBetaHistoryModel(row.lensModel) ? 'block' : 'none'}">
            <h3>超主点β既往情况</h3>
            <div class="beta-history-grid">
              <div class="beta-history-item">
                <div class="beta-history-label">是否有使用阿托品滴眼液<span class="required-star">*</span></div>
                <div class="beta-history-radio" role="radiogroup" aria-label="是否有使用阿托品滴眼液">
                  <label><input type="radio" name="edit-atropineEyeDropsUsed" value="是" ${row.atropineEyeDropsUsed === '是' ? 'checked' : ''}> 是</label>
                  <label><input type="radio" name="edit-atropineEyeDropsUsed" value="否" ${row.atropineEyeDropsUsed === '否' ? 'checked' : ''}> 否</label>
                </div>
                <div class="beta-history-duration" id="edit-atropineEyeDropsDuration">
                  <div class="beta-history-sublabel">停药时长<span class="required-star">*</span></div>
                  <div class="beta-duration-control">
                    <select class="el-input__inner" id="edit-atropineEyeDropsStopMonths">${buildDurationOptions('months', atropineDuration.months)}</select>
                    <span class="beta-duration-unit">个月</span>
                    <select class="el-input__inner" id="edit-atropineEyeDropsStopDays">${buildDurationOptions('days', atropineDuration.days)}</select>
                    <span class="beta-duration-unit">天</span>
                    <span class="beta-history-elapsed" id="edit-atropineEyeDropsElapsed" ${atropineDuration.months === '' && atropineDuration.days === '' ? 'hidden' : ''}>已停药 ${row.atropineEyeDropsStopDuration || ''}</span>
                  </div>
                </div>
              </div>
              <div class="beta-history-item">
                <div class="beta-history-label">是否配戴过角膜塑形镜（OK镜）<span class="required-star">*</span></div>
                <div class="beta-history-radio" role="radiogroup" aria-label="是否配戴过角膜塑形镜">
                  <label><input type="radio" name="edit-orthokeratologyLensUsed" value="是" ${row.orthokeratologyLensUsed === '是' ? 'checked' : ''}> 是</label>
                  <label><input type="radio" name="edit-orthokeratologyLensUsed" value="否" ${row.orthokeratologyLensUsed === '否' ? 'checked' : ''}> 否</label>
                </div>
                <div class="beta-history-duration" id="edit-orthokeratologyLensDuration">
                  <div class="beta-history-sublabel">停戴时长<span class="required-star">*</span></div>
                  <div class="beta-duration-control">
                    <select class="el-input__inner" id="edit-orthokeratologyLensStopMonths">${buildDurationOptions('months', orthokeratologyDuration.months)}</select>
                    <span class="beta-duration-unit">个月</span>
                    <select class="el-input__inner" id="edit-orthokeratologyLensStopDays">${buildDurationOptions('days', orthokeratologyDuration.days)}</select>
                    <span class="beta-duration-unit">天</span>
                    <span class="beta-history-elapsed" id="edit-orthokeratologyLensElapsed" ${orthokeratologyDuration.months === '' && orthokeratologyDuration.days === '' ? 'hidden' : ''}>已停戴 ${row.orthokeratologyLensStopDuration || ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        <!-- 第二部分：常规验光和超主点验光 -->
        <div class="form-section two-columns">
          <!-- 常规验光 -->
          <div class="column regular-refraction-column">
            <h3>常规验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdSphere || ''}" id="edit-regularOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdCylinder || ''}" id="edit-regularOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdAxis || ''}" id="edit-regularOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdVision || ''}" id="edit-regularOdVision">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsSphere || ''}" id="edit-regularOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsCylinder || ''}" id="edit-regularOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsAxis || ''}" id="edit-regularOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsVision || ''}" id="edit-regularOsVision">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼融像：</label>
                <input type="text" class="el-input__inner" value="${row.regularBinocularFusion || ''}" id="edit-regularBinocularFusion">
              </div>
              <div class="form-item">
                <label>立体视功能：</label>
                <input type="text" class="el-input__inner" value="${row.regularStereoscopicVisionFunction || ''}" id="edit-regularStereoscopicVisionFunction">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.regularBinocularVision || ''}" id="edit-regularBinocularVision">
              </div>
            </div>
          </div>

          <!-- 散瞳验光 -->
          <div class="column cycloplegic-refraction-column">
            <h3>散瞳验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdSphere || ''}" id="edit-cycloplegicOdSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdCylinder || ''}" id="edit-cycloplegicOdCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdAxis || ''}" id="edit-cycloplegicOdAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdVision || ''}" id="edit-cycloplegicOdVision"></div></div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsSphere || ''}" id="edit-cycloplegicOsSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsCylinder || ''}" id="edit-cycloplegicOsCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsAxis || ''}" id="edit-cycloplegicOsAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsVision || ''}" id="edit-cycloplegicOsVision"></div></div>
              </div>
            </div>
          </div>

          <!-- 超主点验光 -->
          <div class="column trial-refraction-column">
            <h3>超主点验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdSphere || ''}" id="edit-superOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdCylinder || ''}" id="edit-superOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdAxis || ''}" id="edit-superOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdVision || ''}" id="edit-superOdVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner" value="${row.odQuantity || ''}" id="edit-odQuantity">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsSphere || ''}" id="edit-superOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsCylinder || ''}" id="edit-superOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsAxis || ''}" id="edit-superOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力*：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsVision || ''}" id="edit-superOsVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner" value="${row.osQuantity || ''}" id="edit-osQuantity">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼融像：</label>
                <input type="text" class="el-input__inner" value="${row.superBinocularFusion || ''}" id="edit-superBinocularFusion">
              </div>
              <div class="form-item">
                <label>立体视功能：</label>
                <input type="text" class="el-input__inner" value="${row.superStereoscopicVisionFunction || ''}" id="edit-superStereoscopicVisionFunction">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.superBinocularVision || ''}" id="edit-superBinocularVision">
              </div>
            </div>
          </div>
        </div>

        <!-- 第三部分：角膜曲率和眼镜参数 -->
        <div class="form-section two-columns">
          <!-- 角膜曲率 -->
          <div class="column">
            <h3>角膜曲率</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1*：</label>
                    <input type="text" class="el-input__inner" value="${row.odK1 || ''}" id="edit-odK1">
                  </div>
                  <div class="form-item">
                    <label>K2*：</label>
                    <input type="text" class="el-input__inner" value="${row.odK2 || ''}" id="edit-odK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴*：</label>
                    <input type="text" class="el-input__inner" value="${row.odAxialLength || ''}" id="edit-odAxialLength">
                  </div>
                  <div class="form-item">
                    <label>脉络膜厚度：</label>
                    <input type="text" class="el-input__inner" value="${row.odChoroidalThickness || ''}" id="edit-odChoroidalThickness">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1*：</label>
                    <input type="text" class="el-input__inner" value="${row.osK1 || ''}" id="edit-osK1">
                  </div>
                  <div class="form-item">
                    <label>K2*：</label>
                    <input type="text" class="el-input__inner" value="${row.osK2 || ''}" id="edit-osK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴*：</label>
                    <input type="text" class="el-input__inner" value="${row.osAxialLength || ''}" id="edit-osAxialLength">
                  </div>
                  <div class="form-item">
                    <label>脉络膜厚度：</label>
                    <input type="text" class="el-input__inner" value="${row.osChoroidalThickness || ''}" id="edit-osChoroidalThickness">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 眼镜参数 -->
          <div class="column">
            <h3>眼镜参数</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高*：</label>
                    <input type="text" class="el-input__inner" value="${row.odPupilHeight || ''}" id="edit-odPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距*：</label>
                    <input type="text" class="el-input__inner" value="${row.odPupilDistance || ''}" id="edit-odPupilDistance">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高*：</label>
                    <input type="text" class="el-input__inner" value="${row.osPupilHeight || ''}" id="edit-osPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距*：</label>
                    <input type="text" class="el-input__inner" value="${row.osPupilDistance || ''}" id="edit-osPupilDistance">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼瞳距：</label>
                <input type="text" class="el-input__inner" value="${row.binocularPupilDistance || ''}" id="edit-binocularPupilDistance">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>镜框编号：</label>
                <input type="text" class="el-input__inner" value="${row.frameNumber || ''}" id="edit-frameNumber">
              </div>
              <div class="form-item">
                <label>镜框类型：</label>
                <select class="el-input__inner" id="edit-frameType">
                  <option value="原片" ${row.frameType === '来框加工' || row.frameType === '豪赋镜框加工' ? '' : 'selected'}>原片</option>
                  <option value="来框加工" ${row.frameType === '来框加工' ? 'selected' : ''}>来框加工</option>
                  <option value="豪赋镜框加工" ${row.frameType === '豪赋镜框加工' ? 'selected' : ''}>豪赋镜框加工</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <!-- 第五部分：邮寄信息 -->
        <div class="form-section delivery-info">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人*：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-addressee">
            </div>
            <div class="form-item">
              <label>联系电话*：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-trackingNumber">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址*：</label>
              <textarea class="el-textarea__inner" id="edit-shippingAddress">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-remarks">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    // 立即绑定数字校验（弹框已渲染，但用户尚未点击确定）
    setTimeout(() => {
      setupBetaHistoryForm();
      wrapRequiredStars(document.querySelector('.el-message-box.edit-order-dialog'));
      const candidateInputs = document.querySelectorAll('.edit-order-dialog input.el-input__inner');
      candidateInputs.forEach(input => {
        const id = input.id || '';
        if (/(Sphere|Cylinder|Axis|K1|K2|AxialLength|ChoroidalThickness|Pupil|Distance|Height|Quantity)/i.test(id)) {
          input.classList.add('numeric-input');
        }
      });
      import('@/utils/opticalDataUtil').then(({ validateNumericInput }) => {
        document.querySelectorAll('.numeric-input').forEach(input => {
          const label = input.previousElementSibling?.textContent.replace('*：', '').replace('：', '') || '数值';
          input.addEventListener('input', function() {
            this.value = validateNumericInput(this.value, label, false);
          });
          input.addEventListener('blur', function() {
            this.value = validateNumericInput(this.value, label, true);
          });
        });
      });

      // 设置输入最大长度
      const maxlengthMapGlobal = {
        // 超主点订单字段
        'edit-patientName': 30,
        'edit-age': 10,
        'edit-optometrist': 10,
        'edit-region': 55,
        'edit-lensModel': 30,
        'edit-company': 30,
        'edit-purchasingUnits': 30,
        'edit-frameNumber': 30,
        'edit-binocularFusion': 60,
        'edit-stereoscopicVisionFunction': 60,
        'edit-addressee': 10,
        'edit-phone': 15,
        'edit-regularOdVision': 55,
        'edit-regularOsVision': 55,
        'edit-regularBinocularVision': 55,
        'edit-superOdVision': 55,
        'edit-superOsVision': 55,
        'edit-superBinocularVision': 55,
      };
      Object.entries(maxlengthMapGlobal).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    }, 80);

    // 等待用户点击确定/取消
    await confirmPromise;

    if (isAuthRedirecting()) {
      return;
    }

    // 获取所有表单数据
    const orderData = {
      id: document.getElementById('edit-id').value,
      userId: document.getElementById('edit-userId').value,
      orderNumber: document.getElementById('edit-orderNumber').value,
      status: document.getElementById('edit-status').value,
      orderType: document.getElementById('edit-orderType').value,
      orderDate: document.getElementById('edit-orderDate').value,
      patientName: document.getElementById('edit-patientName').value,
      age: document.getElementById('edit-age').value || null,
      optometrist: document.getElementById('edit-optometrist').value || null,
      region: document.getElementById('edit-region').value || null,
      lensModel: document.getElementById('edit-lensModel').value,
      company: document.getElementById('edit-company').value || null,
      purchasingUnits: document.getElementById('edit-purchasingUnits').value || null,
      atropineEyeDropsUsed: getBetaHistoryUsedValue('edit-atropineEyeDropsUsed'),
      atropineEyeDropsStopDuration: getDurationValue('edit-atropineEyeDropsUsed', 'edit-atropineEyeDropsStopMonths', 'edit-atropineEyeDropsStopDays'),
      orthokeratologyLensUsed: getBetaHistoryUsedValue('edit-orthokeratologyLensUsed'),
      orthokeratologyLensStopDuration: getDurationValue('edit-orthokeratologyLensUsed', 'edit-orthokeratologyLensStopMonths', 'edit-orthokeratologyLensStopDays'),

      // 常规验光 - 右眼
      regularOdSphere: document.getElementById('edit-regularOdSphere').value || null,
      regularOdCylinder: document.getElementById('edit-regularOdCylinder').value || null,
      regularOdAxis: document.getElementById('edit-regularOdAxis').value || null,
      regularOdVision: document.getElementById('edit-regularOdVision').value || null,
      // 常规验光 - 左眼
      regularOsSphere: document.getElementById('edit-regularOsSphere').value || null,
      regularOsCylinder: document.getElementById('edit-regularOsCylinder').value || null,
      regularOsAxis: document.getElementById('edit-regularOsAxis').value || null,
      regularOsVision: document.getElementById('edit-regularOsVision').value || null,
      regularBinocularVision: document.getElementById('edit-regularBinocularVision').value || null,
      regularBinocularFusion: document.getElementById('edit-regularBinocularFusion').value || null,
      regularStereoscopicVisionFunction: document.getElementById('edit-regularStereoscopicVisionFunction').value || null,
      // 散瞳验光
      cycloplegicOdSphere: document.getElementById('edit-cycloplegicOdSphere').value || null,
      cycloplegicOdCylinder: document.getElementById('edit-cycloplegicOdCylinder').value || null,
      cycloplegicOdAxis: document.getElementById('edit-cycloplegicOdAxis').value || null,
      cycloplegicOdVision: document.getElementById('edit-cycloplegicOdVision').value || null,
      cycloplegicOsSphere: document.getElementById('edit-cycloplegicOsSphere').value || null,
      cycloplegicOsCylinder: document.getElementById('edit-cycloplegicOsCylinder').value || null,
      cycloplegicOsAxis: document.getElementById('edit-cycloplegicOsAxis').value || null,
      cycloplegicOsVision: document.getElementById('edit-cycloplegicOsVision').value || null,
      // 超主点验光 - 右眼
      superOdSphere: document.getElementById('edit-superOdSphere').value || null,
      superOdCylinder: document.getElementById('edit-superOdCylinder').value || null,
      superOdAxis: document.getElementById('edit-superOdAxis').value || null,
      superOdVision: document.getElementById('edit-superOdVision').value || null,
      odQuantity: document.getElementById('edit-odQuantity').value || null,
      // 超主点验光 - 左眼
      superOsSphere: document.getElementById('edit-superOsSphere').value || null,
      superOsCylinder: document.getElementById('edit-superOsCylinder').value || null,
      superOsAxis: document.getElementById('edit-superOsAxis').value || null,
      superOsVision: document.getElementById('edit-superOsVision').value || null,
      osQuantity: document.getElementById('edit-osQuantity').value || null,
      superBinocularVision: document.getElementById('edit-superBinocularVision').value || null,
      superBinocularFusion: document.getElementById('edit-superBinocularFusion').value || null,
      superStereoscopicVisionFunction: document.getElementById('edit-superStereoscopicVisionFunction').value || null,
      // 角膜曲率 - 右眼
      odK1: document.getElementById('edit-odK1').value || null,
      odK2: document.getElementById('edit-odK2').value || null,
      odAxialLength: document.getElementById('edit-odAxialLength').value || null,
      odChoroidalThickness: document.getElementById('edit-odChoroidalThickness').value || null,
      // 角膜曲率 - 左眼
      osK1: document.getElementById('edit-osK1').value || null,
      osK2: document.getElementById('edit-osK2').value || null,
      osAxialLength: document.getElementById('edit-osAxialLength').value || null,
      osChoroidalThickness: document.getElementById('edit-osChoroidalThickness').value || null,
      // 眼镜参数 - 右眼
      odPupilHeight: document.getElementById('edit-odPupilHeight').value || null,
      odPupilDistance: document.getElementById('edit-odPupilDistance').value || null,
      // 眼镜参数 - 左眼
      osPupilHeight: document.getElementById('edit-osPupilHeight').value || null,
      osPupilDistance: document.getElementById('edit-osPupilDistance').value || null,
      binocularPupilDistance: document.getElementById('edit-binocularPupilDistance').value || null,
      // 镜框类型和编号
      frameNumber: document.getElementById('edit-frameNumber').value || null,
      frameType: document.getElementById('edit-frameType').value || null,
      // 邮寄信息
      addressee: document.getElementById('edit-addressee').value || null,
      phone: document.getElementById('edit-phone').value || null,
      trackingNumber: document.getElementById('edit-trackingNumber').value || null,
      shippingAddress: document.getElementById('edit-shippingAddress').value || null,
      remarks: document.getElementById('edit-remarks').value || null
    };
    const validateFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 自动填充柱镜/轴位默认值
    fillDefaultZeroFields(orderData);
    // 编辑保存前：对数视力 → 小数视力（方案B）
    convertVisionFieldsInForm(orderData);

    // 定义验证规则
    const validationRules = [
      { field: 'patientName', message: '请输入患者姓名' },
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'purchasingUnits', message: '请输入采购单位' },
      { field: 'age', message: '请输入年龄' },
      { field: 'regularOdSphere', message: '请输入常规验光右眼球镜' },
      { field: 'regularOdVision', message: '请输入常规验光右眼视力' },
      { field:'regularOdCylinder', message: '请输入常规验光右眼柱镜'},
      { field:'regularOdAxis', message: '请输入常规验光右眼轴位' },
      { field: 'regularOsSphere', message: '请输入常规验光左眼球镜' },
      { field: 'regularOsVision', message: '请输入常规验光左眼视力' },
      { field: 'regularOsAxis', message: '请输入常规验光左眼轴位'},
      { field:'regularOsCylinder', message: '请输入常规验光左眼柱镜'},
      { field: 'superOdSphere', message: '请输入超主点验光右眼球镜' },
      { field: 'superOdAxis', message: '请输入超主点验光右眼轴位'},
      { field:'superOdCylinder', message: '请输入超主点验光右眼柱镜'},
      { field: 'superOdVision', message: '请输入超主点验光右眼视力' },
      { field: 'superOsSphere', message: '请输入超主点验光左眼球镜' },
      { field: 'superOsAxis', message: '请输入超主点验光左眼轴位'},
      { field:'superOsCylinder', message: '请输入超主点验光左眼柱镜'},
      { field: 'superOsVision', message: '请输入超主点验光左眼视力' },
      { field: 'odK1', message: '请输入角膜曲率右眼K1' },
      { field: 'odK2', message: '请输入角膜曲率右眼K2' },
      { field: 'odAxialLength', message: '请输入角膜曲率右眼眼轴' },
      { field: 'osK1', message: '请输入角膜曲率左眼K1' },
      { field: 'osK2', message: '请输入角膜曲率左眼K2' },
      { field: 'osAxialLength', message: '请输入角膜曲率左眼眼轴' },
      { field: 'odPupilHeight', message: '请输入右眼瞳高' },
      { field: 'osPupilHeight', message: '请输入左眼瞳高' },
      { field: 'odPupilDistance', message: '请输入右眼瞳距' },
      { field: 'osPupilDistance', message: '请输入左眼瞳距' },
      { field:'addressee', message: '请输入收件人姓名'},
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'phone', message: '请输入联系电话'}
    ];

    // 执行验证
    if (!validateFields(orderData, validationRules)) {
      return;
    }

    if (isBetaHistoryModel(orderData.lensModel)) {
      if (!['是', '否'].includes(orderData.atropineEyeDropsUsed)) {
        ElMessage.error('请选择是否有使用阿托品滴眼液');
        return;
      }
      if (orderData.atropineEyeDropsUsed === '是' && !orderData.atropineEyeDropsStopDuration) {
        ElMessage.error('请选择阿托品滴眼液停药时长');
        return;
      }
      if (!['是', '否'].includes(orderData.orthokeratologyLensUsed)) {
        ElMessage.error('请选择是否配戴过角膜塑形镜（OK镜）');
        return;
      }
      if (orderData.orthokeratologyLensUsed === '是' && !orderData.orthokeratologyLensStopDuration) {
        ElMessage.error('请选择角膜塑形镜（OK镜）停戴时长');
        return;
      }
    }

    const response = await axios.put(`/api/v1/user/order`, orderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

const handleEditBulk = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 判断是否管理员
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo.role === 'ADMIN';

    // 先获取验配单位列表供下拉使用（批量订单）
    const bulkClinicOptions = await fetchOptometryClinicOptions();
    if (bulkClinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const bulkCompanyOptionsHtml = buildCompanySelectHtml(row.company, bulkClinicOptions);

    const confirmPromise = ElMessageBox.confirm(
        `<div class="edit-order-form">
          <div class="form-section basic-info">
            <h3>批量订单基本信息</h3>
            <div class="form-row">
              <div class="form-item">
                <label>序号：</label>
                <input type="text" class="el-input__inner" value="${displayId}" readonly>
                <input type="hidden" id="edit-bulk-id" value="${row.id || ''}">
                <input type="hidden" id="edit-bulk-orderNumber" value="${row.orderNumber || ''}">
                <input type="hidden" id="edit-bulk-userId" value="${row.userId || ''}">
                <input type="hidden" id="edit-bulk-status" value="${row.status || ''}">
              </div>
              <div class="form-item">
                <label>订单类型：</label>
                <input type="text" class="el-input__inner" value="${row.orderType || '批量订单'}" id="edit-bulk-orderType" readonly>
              </div>
              <div class="form-item">
                <label>订单日期*：</label>
                <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-bulk-orderDate" readonly>
              </div>
              <div class="form-item">
                <label>地区：</label>
                <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-bulk-region">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>验配单位*：</label>
                <select class="el-input__inner" id="edit-bulk-company">
                  <option value="">请选择验配单位</option>
                  ${bulkCompanyOptionsHtml}
                </select>
              </div>
              <div class="form-item">
                <label>采购单位*：</label>
                <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-bulk-purchasingUnits" ${isAdmin ? '' : 'readonly'}>
              </div>
              <div class="form-item">
                <label>订单型号*：</label>
                <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-bulk-lensModel">
              </div>
              <div class="form-item">
                <label>数量*：</label>
                <input type="number" class="el-input__inner" value="${row.quantity || ''}" id="edit-bulk-quantity">
              </div>
            </div>
          </div>
         <!-- 邮寄信息 -->
        <div class="form-section">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人*：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-bulk-addressee">
            </div>
            <div class="form-item">
              <label>联系电话*：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-bulk-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-bulk-trackingNumber">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址*：</label>
              <textarea class="el-textarea__inner" id="edit-bulk-shippingAddress" rows="2">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-bulk-remarks" rows="2">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑批量订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    // 立即绑定批量订单数字校验
    setTimeout(() => {
      wrapRequiredStars(document.querySelector('.el-message-box.edit-order-dialog'));
      const inputs = document.querySelectorAll('.edit-order-dialog input.el-input__inner');
      inputs.forEach(i => {
        if (/edit-bulk-quantity/.test(i.id)) i.classList.add('numeric-input');
      });
      import('@/utils/opticalDataUtil').then(({ validateNumericInput }) => {
        document.querySelectorAll('.numeric-input').forEach(i => {
          const label = i.previousElementSibling?.textContent.replace('*：','').replace('：','')||'数值';
          i.addEventListener('input', e=>{e.target.value = validateNumericInput(e.target.value,label,false);});
        });
      });

      // 设置输入最大长度（批量订单）
      const maxlengthMapBulk = {
        'edit-bulk-region': 50,
        'edit-bulk-company': 30,
        'edit-bulk-purchasingUnits': 30,
        'edit-bulk-lensModel': 30,
        'edit-bulk-quantity': 20,
        'edit-bulk-addressee': 10,
        'edit-bulk-phone': 15
      };
      Object.entries(maxlengthMapBulk).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    },80);

    await confirmPromise;

    if (isAuthRedirecting()) {
      return;
    }

    // 获取所有表单数据
    const bulkOrderData = {
      id: document.getElementById('edit-bulk-id').value,
      userId: document.getElementById('edit-bulk-userId').value,
      orderNumber: document.getElementById('edit-bulk-orderNumber').value,
      status: document.getElementById('edit-bulk-status').value,
      orderDate: document.getElementById('edit-bulk-orderDate').value,
      orderType: document.getElementById('edit-bulk-orderType').value,
      region: document.getElementById('edit-bulk-region').value,
      company: document.getElementById('edit-bulk-company').value,
      purchasingUnits: document.getElementById('edit-bulk-purchasingUnits').value,
      lensModel: document.getElementById('edit-bulk-lensModel').value,
      quantity: document.getElementById('edit-bulk-quantity').value,

      // 邮寄信息
      addressee: document.getElementById('edit-bulk-addressee').value || null,
      phone: document.getElementById('edit-bulk-phone').value || null,
      trackingNumber: document.getElementById('edit-bulk-trackingNumber').value || null,
      shippingAddress: document.getElementById('edit-bulk-shippingAddress').value || null,
      remarks: document.getElementById('edit-bulk-remarks').value || null
    }

    const validateBulkFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 定义验证规则
    const bulkValidationRules = [
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'quantity', message: '请输入数量' },
      { field: 'phone', message: '请输入联系电话'},
      { field: 'addressee', message: '请输入收件人姓名' },
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'purchasingUnits', message: '请输入采购单位'}
    ];

    // 执行验证
    if (!validateBulkFields(bulkOrderData, bulkValidationRules)) {
      return;
    }

    // 新增：数量必须为正整数
    if (!/^[1-9]\d*$/.test(bulkOrderData.quantity)) {
      ElMessage.error('数量必须为正整数');
      return;
    }

    const response = await axios.put(`/api/v1/user/bulkorder`, bulkOrderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

const handleEditFuxiangsan = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 判断是否管理员
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo.role === 'ADMIN';

    // 先获取验配单位列表供下拉使用（赋像散订单）
    const fuxiangsanClinicOptions = await fetchOptometryClinicOptions();
    if (fuxiangsanClinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const fuxiangsanCompanyOptionsHtml = buildCompanySelectHtml(row.company, fuxiangsanClinicOptions);

    const confirmPromise = ElMessageBox.confirm(
        `<div class="edit-order-form">
        <!-- 第一部分：订单基本信息 -->
        <div class="form-section basic-info">
          <h3>订单基本信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>序号：</label>
              <input type="text" class="el-input__inner" value="${displayId}" readonly>
              <input type="hidden" id="edit-fuxiangsan-id" value="${row.id || ''}">
              <input type="hidden" id="edit-fuxiangsan-orderNumber" value="${row.orderNumber || ''}">
              <input type="hidden" id="edit-fuxiangsan-userId" value="${row.userId || ''}">
              <input type="hidden" id="edit-fuxiangsan-status" value="${row.status || ''}">
            </div>
            <div class="form-item">
              <label>订单类型：</label>
              <input type="text" class="el-input__inner" value="${row.orderType || '散订单'}" id="edit-fuxiangsan-orderType" readonly>
            </div>
            <div class="form-item">
              <label>订单日期*：</label>
              <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-fuxiangsan-orderDate" readonly>
            </div>
            <div class="form-item">
              <label>患者姓名*：</label>
              <input type="text" class="el-input__inner" value="${row.patientName || ''}" id="edit-fuxiangsan-patientName">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>年龄*：</label>
              <input type="text" class="el-input__inner" value="${row.age || ''}" id="edit-fuxiangsan-age">
            </div>
            <div class="form-item">
              <label>验光师：</label>
              <input type="text" class="el-input__inner" value="${row.optometrist || ''}" id="edit-fuxiangsan-optometrist">
            </div>
            <div class="form-item">
              <label>地区：</label>
              <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-fuxiangsan-region">
            </div>
            <div class="form-item">
              <label>订单型号*：</label>
              <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-fuxiangsan-lensModel">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>验配单位*：</label>
              <select class="el-input__inner" id="edit-fuxiangsan-company">
                <option value="">请选择验配单位</option>
                ${fuxiangsanCompanyOptionsHtml}
              </select>
            </div>
            <div class="form-item">
              <label>采购单位*：</label>
              <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-fuxiangsan-purchasingUnits" ${isAdmin ? '' : 'readonly'}>
            </div>
          </div>
        </div>

        <!-- 第二部分：常规验光和超主点验光 -->
        <div class="form-section two-columns">
          <!-- 常规验光 -->
          <div class="column regular-refraction-column">
            <h3>常规验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdSphere || ''}" id="edit-fuxiangsan-regularOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdCylinder || ''}" id="edit-fuxiangsan-regularOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdAxis || ''}" id="edit-fuxiangsan-regularOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdVision || ''}" id="edit-fuxiangsan-regularOdVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner" value="${row.odQuantity || ''}" id="edit-fuxiangsan-odQuantity">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsSphere || ''}" id="edit-fuxiangsan-regularOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsCylinder || ''}" id="edit-fuxiangsan-regularOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位*：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsAxis || ''}" id="edit-fuxiangsan-regularOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsVision || ''}" id="edit-fuxiangsan-regularOsVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner" value="${row.osQuantity || ''}" id="edit-fuxiangsan-osQuantity">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.regularBinocularVision || ''}" id="edit-fuxiangsan-regularBinocularVision">
              </div>
            </div>
          </div>

          <!-- 散瞳验光 -->
          <div class="column cycloplegic-refraction-column">
            <h3>散瞳验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdSphere || ''}" id="edit-fuxiangsan-cycloplegicOdSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdCylinder || ''}" id="edit-fuxiangsan-cycloplegicOdCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdAxis || ''}" id="edit-fuxiangsan-cycloplegicOdAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdVision || ''}" id="edit-fuxiangsan-cycloplegicOdVision"></div></div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsSphere || ''}" id="edit-fuxiangsan-cycloplegicOsSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsCylinder || ''}" id="edit-fuxiangsan-cycloplegicOsCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsAxis || ''}" id="edit-fuxiangsan-cycloplegicOsAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsVision || ''}" id="edit-fuxiangsan-cycloplegicOsVision"></div></div>
              </div>
            </div>
          </div>

          <!-- 试戴片验光 -->
          <div class="column trial-refraction-column">
            <h3>试戴片验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdSphere || ''}" id="edit-fuxiangsan-superOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdCylinder || ''}" id="edit-fuxiangsan-superOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdAxis || ''}" id="edit-fuxiangsan-superOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdVision || ''}" id="edit-fuxiangsan-superOdVision">
                  </div>

                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsSphere || ''}" id="edit-fuxiangsan-superOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsCylinder || ''}" id="edit-fuxiangsan-superOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsAxis || ''}" id="edit-fuxiangsan-superOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力：</label>
                    <input type="text" class="el-input__inner" value="${row.superOsVision || ''}" id="edit-fuxiangsan-superOsVision">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.superBinocularVision || ''}" id="edit-fuxiangsan-superBinocularVision">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>镜框编号：</label>
                <input type="text" class="el-input__inner" value="${row.frameNumber || ''}" id="edit-fuxiangsan-frameNumber">
              </div>
              <div class="form-item">
                <label>镜框类型<span class="required-star">*</span>：</label>
                <select class="el-input__inner" id="edit-fuxiangsan-frameType">
                  <option value="" ${!row.frameType ? 'selected' : ''} disabled>请选择</option>
                  <option value="原片" ${row.frameType === '原片' ? 'selected' : ''}>原片</option>
                  <option value="来框加工" ${row.frameType === '来框加工' ? 'selected' : ''}>来框加工</option>
                  <option value="豪赋镜框加工" ${row.frameType === '豪赋镜框加工' ? 'selected' : ''}>豪赋镜框加工</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 第三部分：角膜曲率和眼镜参数 -->
        <div class="form-section two-columns">
          <!-- 角膜曲率 -->
          <div class="column">
            <h3>角膜曲率</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1：</label>
                    <input type="text" class="el-input__inner" value="${row.odK1 || ''}" id="edit-fuxiangsan-odK1">
                  </div>
                  <div class="form-item">
                    <label>K2：</label>
                    <input type="text" class="el-input__inner" value="${row.odK2 || ''}" id="edit-fuxiangsan-odK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴：</label>
                    <input type="text" class="el-input__inner" value="${row.odAxialLength || ''}" id="edit-fuxiangsan-odAxialLength">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1：</label>
                    <input type="text" class="el-input__inner" value="${row.osK1 || ''}" id="edit-fuxiangsan-osK1">
                  </div>
                  <div class="form-item">
                    <label>K2：</label>
                    <input type="text" class="el-input__inner" value="${row.osK2 || ''}" id="edit-fuxiangsan-osK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴：</label>
                    <input type="text" class="el-input__inner" value="${row.osAxialLength || ''}" id="edit-fuxiangsan-osAxialLength">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 眼镜参数 -->
          <div class="column">
            <h3>眼镜参数</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高：</label>
                    <input type="text" class="el-input__inner" value="${row.odPupilHeight || ''}" id="edit-fuxiangsan-odPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距：</label>
                    <input type="text" class="el-input__inner" value="${row.odPupilDistance || ''}" id="edit-fuxiangsan-odPupilDistance">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高：</label>
                    <input type="text" class="el-input__inner" value="${row.osPupilHeight || ''}" id="edit-fuxiangsan-osPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距：</label>
                    <input type="text" class="el-input__inner" value="${row.osPupilDistance || ''}" id="edit-fuxiangsan-osPupilDistance">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼瞳距：</label>
                <input type="text" class="el-input__inner" value="${row.binocularPupilDistance || ''}" id="edit-fuxiangsan-binocularPupilDistance">
              </div>
            </div>
          </div>
        </div>
         <!-- 第四部分：眼位功能 -->
        <div class="form-section">
          <h3>眼位</h3>
          <div class="form-row">
            <div class="form-item">
              <label>远：</label>
              <input type="text" class="el-input__inner" value="${row.eyePositionFar || ''}" id="edit-fuxiangsan-eyePositionFar">
            </div>
            <div class="form-item">
              <label>近：</label>
              <input type="text" class="el-input__inner" value="${row.eyePositionNear || ''}" id="edit-fuxiangsan-eyePositionNear">
            </div>
          </div>
        </div>

        <!-- 第五部分：邮寄信息 -->
        <div class="form-section delivery-info">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人*：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-fuxiangsan-addressee">
            </div>
            <div class="form-item">
              <label>联系电话*：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-fuxiangsan-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-fuxiangsan-trackingNumber">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址*：</label>
              <textarea class="el-textarea__inner" id="edit-fuxiangsan-shippingAddress">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-fuxiangsan-remarks">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑其他订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    // ===== 新增：赋像散订单数字校验 =====
    setTimeout(() => {
      wrapRequiredStars(document.querySelector('.el-message-box.edit-order-dialog'));
      const candidateInputs = document.querySelectorAll('.edit-order-dialog input.el-input__inner');
      candidateInputs.forEach(input => {
        const id = input.id || '';
        if (/(Sphere|Cylinder|Axis|K1|K2|AxialLength|Pupil|Distance|Height|Quantity)/i.test(id)) {
          input.classList.add('numeric-input');
        }
      });

      import('@/utils/opticalDataUtil').then(({ validateNumericInput }) => {
        const numericInputs = document.querySelectorAll('.numeric-input');
        numericInputs.forEach(input => {
          const labelEl = input.previousElementSibling;
          const fieldName = labelEl ? labelEl.textContent.replace('*：', '').replace('：', '') : '数值';

          input.addEventListener('input', function() {
            this.value = validateNumericInput(this.value, fieldName, false);
          });
          input.addEventListener('blur', function() {
            this.value = validateNumericInput(this.value, fieldName, true);
          });
        });
      });

      // 设置输入最大长度（赋像散）
      const maxlengthMapFxs = {
        'edit-fuxiangsan-patientName': 30,
        'edit-fuxiangsan-age': 10,
        'edit-fuxiangsan-optometrist': 10,
        'edit-fuxiangsan-region': 55,
        'edit-fuxiangsan-lensModel': 30,
        'edit-fuxiangsan-company': 30,
        'edit-fuxiangsan-purchasingUnits': 30,
        'edit-fuxiangsan-frameNumber': 20,
        'edit-fuxiangsan-odPupilDistance': 10,
        'edit-fuxiangsan-osPupilDistance': 10,
        'edit-fuxiangsan-eyePositionFar': 40,
        'edit-fuxiangsan-eyePositionNear': 40,
        'edit-fuxiangsan-addressee': 10,
        'edit-fuxiangsan-phone': 15
      };
      Object.entries(maxlengthMapFxs).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    }, 100);

    // 等待用户点击确定/取消
    await confirmPromise;

    if (isAuthRedirecting()) {
      return;
    }

    // 获取所有表单数据
    const fuxiangsanOrderData = {
      id: document.getElementById('edit-fuxiangsan-id').value,
      userId: document.getElementById('edit-fuxiangsan-userId').value,
      orderNumber: document.getElementById('edit-fuxiangsan-orderNumber').value,
      status: document.getElementById('edit-fuxiangsan-status').value,
      orderType: document.getElementById('edit-fuxiangsan-orderType').value,
      orderDate: document.getElementById('edit-fuxiangsan-orderDate').value,
      patientName: document.getElementById('edit-fuxiangsan-patientName').value,
      age: document.getElementById('edit-fuxiangsan-age').value || null,
      optometrist: document.getElementById('edit-fuxiangsan-optometrist').value || null,
      region: document.getElementById('edit-fuxiangsan-region').value || null,
      lensModel: document.getElementById('edit-fuxiangsan-lensModel').value,
      company: document.getElementById('edit-fuxiangsan-company').value || null,
      purchasingUnits: document.getElementById('edit-fuxiangsan-purchasingUnits').value || null,

      // 常规验光 - 右眼
      regularOdSphere: document.getElementById('edit-fuxiangsan-regularOdSphere').value || null,
      regularOdCylinder: document.getElementById('edit-fuxiangsan-regularOdCylinder').value || null,
      regularOdAxis: document.getElementById('edit-fuxiangsan-regularOdAxis').value || null,
      regularOdVision: document.getElementById('edit-fuxiangsan-regularOdVision').value || null,
      // 常规验光 - 左眼
      regularOsSphere: document.getElementById('edit-fuxiangsan-regularOsSphere').value || null,
      regularOsCylinder: document.getElementById('edit-fuxiangsan-regularOsCylinder').value || null,
      regularOsAxis: document.getElementById('edit-fuxiangsan-regularOsAxis').value || null,
      regularOsVision: document.getElementById('edit-fuxiangsan-regularOsVision').value || null,
      regularBinocularVision: document.getElementById('edit-fuxiangsan-regularBinocularVision').value || null,
      // 散瞳验光
      cycloplegicOdSphere: document.getElementById('edit-fuxiangsan-cycloplegicOdSphere').value || null,
      cycloplegicOdCylinder: document.getElementById('edit-fuxiangsan-cycloplegicOdCylinder').value || null,
      cycloplegicOdAxis: document.getElementById('edit-fuxiangsan-cycloplegicOdAxis').value || null,
      cycloplegicOdVision: document.getElementById('edit-fuxiangsan-cycloplegicOdVision').value || null,
      cycloplegicOsSphere: document.getElementById('edit-fuxiangsan-cycloplegicOsSphere').value || null,
      cycloplegicOsCylinder: document.getElementById('edit-fuxiangsan-cycloplegicOsCylinder').value || null,
      cycloplegicOsAxis: document.getElementById('edit-fuxiangsan-cycloplegicOsAxis').value || null,
      cycloplegicOsVision: document.getElementById('edit-fuxiangsan-cycloplegicOsVision').value || null,
      // 赋像散验光 - 右眼
      superOdSphere: document.getElementById('edit-fuxiangsan-superOdSphere').value || null,
      superOdCylinder: document.getElementById('edit-fuxiangsan-superOdCylinder').value || null,
      superOdAxis: document.getElementById('edit-fuxiangsan-superOdAxis').value || null,
      superOdVision: document.getElementById('edit-fuxiangsan-superOdVision').value || null,
      odQuantity: document.getElementById('edit-fuxiangsan-odQuantity').value || null,
      // 赋像散验光 - 左眼
      superOsSphere: document.getElementById('edit-fuxiangsan-superOsSphere').value || null,
      superOsCylinder: document.getElementById('edit-fuxiangsan-superOsCylinder').value || null,
      superOsAxis: document.getElementById('edit-fuxiangsan-superOsAxis').value || null,
      superOsVision: document.getElementById('edit-fuxiangsan-superOsVision').value || null,
      osQuantity: document.getElementById('edit-fuxiangsan-osQuantity').value || null,
      superBinocularVision: document.getElementById('edit-fuxiangsan-superBinocularVision').value || null,
      // 角膜曲率 - 右眼
      odK1: document.getElementById('edit-fuxiangsan-odK1').value || null,
      odK2: document.getElementById('edit-fuxiangsan-odK2').value || null,
      odAxialLength: document.getElementById('edit-fuxiangsan-odAxialLength').value || null,
      // 角膜曲率 - 左眼
      osK1: document.getElementById('edit-fuxiangsan-osK1').value || null,
      osK2: document.getElementById('edit-fuxiangsan-osK2').value || null,
      osAxialLength: document.getElementById('edit-fuxiangsan-osAxialLength').value || null,
      // 眼镜参数 - 右眼
      odPupilHeight: document.getElementById('edit-fuxiangsan-odPupilHeight').value || null,
      odPupilDistance: document.getElementById('edit-fuxiangsan-odPupilDistance').value || null,
      // 眼镜参数 - 左眼
      osPupilHeight: document.getElementById('edit-fuxiangsan-osPupilHeight').value || null,
      osPupilDistance: document.getElementById('edit-fuxiangsan-osPupilDistance').value || null,
      binocularPupilDistance: document.getElementById('edit-fuxiangsan-binocularPupilDistance').value || null,
      frameNumber: document.getElementById('edit-fuxiangsan-frameNumber').value || null,
      frameType: document.getElementById('edit-fuxiangsan-frameType').value || null,
      // 眼位
      eyePositionFar: document.getElementById('edit-fuxiangsan-eyePositionFar').value || null,
      eyePositionNear: document.getElementById('edit-fuxiangsan-eyePositionNear').value || null,

      // 邮寄信息
      addressee: document.getElementById('edit-fuxiangsan-addressee').value || null,
      phone: document.getElementById('edit-fuxiangsan-phone').value || null,
      trackingNumber: document.getElementById('edit-fuxiangsan-trackingNumber').value || null,
      shippingAddress: document.getElementById('edit-fuxiangsan-shippingAddress').value || null,
      remarks: document.getElementById('edit-fuxiangsan-remarks').value || null
    };

    const validateFuxiangsanFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 自动填充柱镜/轴位默认值
    fillDefaultZeroFields(fuxiangsanOrderData);
    // 编辑保存前：对数视力 → 小数视力（方案B）
    convertVisionFieldsInForm(fuxiangsanOrderData);

    // 定义验证规则
    const fuxiangsanValidationRules = [
      { field: 'patientName', message: '请输入患者姓名' },
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'purchasingUnits', message: '请输入采购单位' },
      { field: 'age', message: '请输入年龄' },
      { field: 'regularOdSphere', message: '请输入常规验光右眼球镜' },
      { field: 'regularOdCylinder',message: '请输入常规验光右眼柱镜' },
      { field:'regularOdAxis', message: '请输入常规验光右眼轴位' },
      { field: 'regularOsSphere', message: '请输入常规验光左眼球镜' },
      { field:'regularOsCylinder', message: '请输入常规验光左眼柱镜' },
      { field:'regularOsAxis', message: '请输入常规验光左眼轴位' },
      { field: 'frameType', message: '请选择镜框类型' },
      { field:'addressee', message: '请输入收件人姓名'},
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'phone', message: '请输入联系电话'}
    ];

    // 执行验证
    if (!validateFuxiangsanFields(fuxiangsanOrderData, fuxiangsanValidationRules)) {
      return;
    }

    const response = await axios.put(`/api/v1/user/scatterorder`, fuxiangsanOrderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

// 自动将标签中的"*"包裹为<span class="required-star">*</span>
const wrapRequiredStars = (container) => {
  if (!container) return;
  container.querySelectorAll('label').forEach(label => {
    if (label.innerHTML.includes('*') && !label.querySelector('.required-star')) {
      // 仅替换第一个星号，避免意外重复
      label.innerHTML = label.innerHTML.replace('*', '<span class="required-star">*</span>');
    }
  });
};

defineExpose({
  handleEdit,
  handleEditBulk,
  handleEditFuxiangsan
});
</script>
